import { Box, BoxProps, ResponsiveValue } from "@chakra-ui/react"
import { keyframes } from "@emotion/react"
import { isArray, isObject, mapValues } from "lodash"
import React from "react"

export const SPINNER_SIZES = {
  xsmall: 3,
  small: 4,
  medium: 6,
  large: 8,
  xlarge: 12,
} as const

type SpinnerProps = Omit<BoxProps, "size"> & {
  size?: ResponsiveValue<keyof typeof SPINNER_SIZES>
}

const spin = keyframes`
  0% {
    rotate: 0deg;
  }
  100% {
    rotate: 360deg;
  }
`

export const Spinner: React.FC<SpinnerProps> = ({
  size = "medium",
  ...props
}) => {
  const boxSize = toBoxSize(size)

  return (
    <Box
      as="svg"
      data-size={size}
      viewBox={`-24 -24 48 48`}
      boxSize={boxSize}
      {...props}
    >
      <Box
        as="circle"
        cx={0}
        cy={0}
        r={21}
        stroke="ds.border.default"
        strokeWidth={6}
        fill="none"
      />
      <Box as="g" css={{ animation: `${spin} 0.75s linear infinite` }}>
        <Box
          as="path"
          d={`M0 -21A21 21 0 0 1 0 21`}
          stroke="ds.icon.accent.teal"
          strokeWidth={6}
          fill="none"
        />
      </Box>
    </Box>
  )
}

const toBoxSize = (
  size: ResponsiveValue<keyof typeof SPINNER_SIZES>
): BoxProps["boxSize"] => {
  if (isArray(size)) {
    return size.map((s) => SPINNER_SIZES[s ?? "medium"])
  }

  if (isObject(size)) {
    return mapValues(size, (s) => SPINNER_SIZES[s ?? "medium"])
  }

  return SPINNER_SIZES[size]
}
